// krogerCheckController.js - Admin CRM Kroger Check (#15): runs adminKrogerCheck and shows each step (token, stores,
// products, the sign-in return address) with how long it took, and the address our functions call Kroger from.
import { BADGE, CARD, EMPTY_STATE } from "./ui/classes.js";
import { withBusy } from "./ui/busy.js";

const STEP_TITLES = {
  token: "Application token",
  stores: "Stores near the ZIP code",
  products: "Product search at the nearest store",
  callback: "Sign-in return address"
};

// Kroger's stock levels as formatProduct names them.
const STOCK_LABELS = { in_stock: "in stock", low: "low stock", out: "out of stock", unknown: "stock unknown" };

let activeDeps = null;

function escapeHTML(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function money(price) {
  if (!price || typeof price.regular !== "number") return "—";
  const regular = `$${price.regular.toFixed(2)}`;
  return typeof price.promo === "number" ? `$${price.promo.toFixed(2)} <span class="text-gray-400 line-through">${regular}</span>` : regular;
}

function ways(f) {
  if (!f) return "";
  const list = [f.pickup && "pickup", f.delivery && "delivery"].filter(Boolean);
  return list.length ? list.join(", ") : "not sold online";
}

function stepBody(step) {
  if (!step.ok) {
    const how = [step.code, step.status].filter(Boolean).join(" · ");
    return `<p class="text-sm text-red-600 dark:text-red-400">${escapeHTML(step.error)}${how ? ` <span class="font-mono text-xs">(${escapeHTML(how)})</span>` : ""}</p>`;
  }
  switch (step.name) {
    case "token":
      return `<p class="text-sm text-gray-600 dark:text-gray-300">Kroger issued a token${step.expiresIn ? `, valid ${Math.round(step.expiresIn / 60)} minutes` : ""}.</p>`;
    case "stores":
      if (!step.stores.length) return `<p class="text-sm text-gray-600 dark:text-gray-300">Kroger answered, with no store within 50 miles.</p>`;
      return `<ul class="divide-y divide-gray-100 dark:divide-white/5">${step.stores.map((s) => `
        <li class="flex flex-wrap items-baseline justify-between gap-x-4 py-2 text-sm">
          <span class="text-gray-900 dark:text-white">${escapeHTML(s.name)}</span>
          <span class="text-gray-500 dark:text-gray-400">${escapeHTML(s.address)} <span class="ml-2 font-mono text-xs">${escapeHTML(s.locationId)}</span></span>
        </li>`).join("")}</ul>`;
    case "products":
      if (!step.products.length) return `<p class="text-sm text-gray-600 dark:text-gray-300">Kroger answered, with no product for that search at ${escapeHTML(step.store.name)}.</p>`;
      return `<p class="mb-2 text-xs text-gray-500 dark:text-gray-400">At ${escapeHTML(step.store.name)}</p>
        <ul class="divide-y divide-gray-100 dark:divide-white/5">${step.products.map((p) => `
        <li class="flex flex-wrap items-baseline justify-between gap-x-4 py-2 text-sm">
          <span class="text-gray-900 dark:text-white">${escapeHTML(p.description)}${p.size ? ` <span class="text-gray-500 dark:text-gray-400">${escapeHTML(p.size)}</span>` : ""}</span>
          <span class="text-gray-500 dark:text-gray-400">${money(p.price)} · ${escapeHTML(STOCK_LABELS[p.stock] || p.stock)} · ${escapeHTML(ways(p.fulfillment))}</span>
        </li>`).join("")}</ul>`;
    case "callback":
      return `<p class="text-sm text-gray-600 dark:text-gray-300">Redirects to the app: <span class="font-mono text-xs">${escapeHTML(step.location)}</span></p>`;
    default:
      return "";
  }
}

export function renderKrogerCheck(result) {
  const failed = result.steps.filter((s) => !s.ok).length;
  const summary = failed
    ? `<span class="${BADGE.red}">${failed} of ${result.steps.length} steps failed</span>`
    : `<span class="${BADGE.green}">Kroger answers production</span>`;
  const header = `
    <div class="flex flex-wrap items-center gap-3">
      ${summary}
      <span class="text-sm text-gray-500 dark:text-gray-400">Our functions call Kroger from <span class="font-mono">${escapeHTML(result.egressIp || "an unknown address")}</span></span>
    </div>`;
  const cards = result.steps.map((step) => `
    <div class="${CARD.panel}">
      <div class="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-white/10">
        <h4 class="${CARD.title}">${escapeHTML(STEP_TITLES[step.name] || step.name)}</h4>
        <span class="flex items-center gap-2">
          <span class="text-xs text-gray-500 tabular-nums dark:text-gray-400">${step.ms} ms</span>
          <span class="${step.ok ? BADGE.green : BADGE.red}">${step.ok ? "OK" : "Failed"}</span>
        </span>
      </div>
      <div class="px-4 py-3">${stepBody(step)}</div>
    </div>`).join("");
  return header + cards;
}

function renderEmpty() {
  const results = document.getElementById("krogerCheckResults");
  if (!results) return;
  results.innerHTML = `
    <div class="${EMPTY_STATE.box}">
      <p class="${EMPTY_STATE.title}">Run the check to see whether Kroger answers our production functions.</p>
    </div>`;
}

async function runCheck() {
  if (!activeDeps) return;
  const { functions, httpsCallable, showToast } = activeDeps;
  const zipCode = (document.getElementById("krogerCheckZip")?.value || "").trim();
  const term = (document.getElementById("krogerCheckTerm")?.value || "").trim();
  if (!/^\d{5}$/.test(zipCode) || !term) return;

  await withBusy(document.getElementById("krogerCheckRunBtn"), async () => {
    try {
      const { data } = await httpsCallable(functions, "adminKrogerCheck")({ zipCode, term });
      const results = document.getElementById("krogerCheckResults");
      if (results) results.innerHTML = renderKrogerCheck(data);
    } catch (err) {
      console.error("❌ Kroger check failed:", err);
      showToast?.(`Could not run the Kroger check: ${err.message}`, "error");
    }
  });
}

export async function loadKrogerCheckTemplate() {
  const containerEl = document.getElementById("sectionKrogerCheckContainer");
  if (!containerEl) return false;
  try {
    const res = await fetch("templates/krogerCheck.html");
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching krogerCheck.html`);
    containerEl.innerHTML = await res.text();
    return true;
  } catch (err) {
    console.error("❌ Failed to load Kroger check template:", err);
    return false;
  }
}

// Binds the form and reads nothing: the template is mounted before anyone has signed in (#1333), and Kroger is
// called only when the admin runs the check.
export function initKrogerCheckController(deps) {
  activeDeps = deps;
  renderEmpty();
  document.getElementById("krogerCheckForm")?.addEventListener("submit", (e) => {
    e.preventDefault();
    runCheck();
  });
}
